import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	ArrowUpOutline,
	Box,
	Button,
	ChatAlt2Outline,
	CheckOutline,
	CheveronSmallDownOutline,
	DotsHorizontalOutline,
	DuplicateOutline,
	Flex,
	GlobeAltOutline,
	HStack,
	IconButton,
	Input,
	LightBulbOutline,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	MicrophoneOutline,
	PencilAltOutline,
	PencilOutline,
	SearchOutline,
	SparklesOutline,
	Spinner,
	StopSolid,
	Text,
	Textarea,
	Tooltip,
	TrashOutline,
	useDisclosure,
	useToast,
	XOutline,
} from '@bonusly/design-system';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { useBizyActivationState, useBizyFeedbackMutation, useBizyTitleMutation } from '../../queries/bizy';
import {
	bizyUserConversationsQueryKey,
	useBizyDeleteConversationMutation,
	useBizyUpdateConversationMutation,
	useBizyUserConversations,
} from '../../queries/bizy-conversations';
import { Bizy, type BizyState } from '../bizy';
import { BizyAmbientGlow } from './bizy-ambient-glow';
import { BizySidePanelHeader } from './bizy-side-panel-header';
import { BizySidePanelSuggestedPrompts } from './bizy-side-panel-suggested-prompts';
import { BizyFeedbackModal } from './components/bizy-feedback-modal';
import { BizyMessageFeedback } from './components/bizy-message-feedback';
import { BizyNotesEditMessage } from './components/bizy-notes-edit-message';
import { BizyStreamingMessage } from './components/bizy-streaming-message';
import { useBizyExternalOpenAutoSend } from './hooks/use-bizy-external-open-auto-send';
import { useBizyGreeting } from './hooks/use-bizy-greeting';
import { useBizySidePanelChat } from './hooks/use-bizy-side-panel-chat';
import { useBizyVoiceInput } from './hooks/use-bizy-voice-input';
import { generateConversationTitle, getActiveConversationTitle } from './lib/conversation-title';
import { getCachedTitle, removeCachedTitle, setCachedTitle } from './lib/conversation-title-cache';
import { getToolProgressKey, isNotesEdit } from './lib/notes-edit-detection';
import { clearBizySession } from './lib/session-storage';
import { getAudienceSegment, getPromptChips } from './lib/suggested-prompts';
import { useBizyPageContextStore } from './store/use-bizy-page-context-store';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';

const chatActionIconStyles = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

function formatMessageTime(raw: Date | string): string {
	const date = raw instanceof Date ? raw : new Date(raw);
	const now = new Date();
	const isToday = date.toDateString() === now.toDateString();
	if (isToday) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
	return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function UserMessageActions({ content, timestamp }: { content: string; timestamp?: Date }) {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const handleCopy = useCallback(() => {
		navigator.clipboard
			.writeText(content)
			.then(() => {
				setCopied(true);
				if (timeoutRef.current) clearTimeout(timeoutRef.current);
				timeoutRef.current = setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {
				toast({ title: t('feedback.copyFailed'), status: 'error', duration: 2000 });
			});
	}, [content, toast, t]);

	return (
		<Flex align="center" gap="4px" justifyContent="flex-end">
			{timestamp && (
				<Text fontSize="xs" color="text.softest" mr={1}>
					{formatMessageTime(timestamp)}
				</Text>
			)}
			<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
				<IconButton
					aria-label={t('feedback.copy')}
					icon={copied ? <CheckOutline boxSize={5} /> : <DuplicateOutline boxSize={5} />}
					{...chatActionIconStyles}
					onClick={handleCopy}
				/>
			</Tooltip>
		</Flex>
	);
}

const RECENT_CONVERSATIONS_LIMIT = 5;

interface BizySidePanelChatViewProps {
	onOpenHistory: () => void;
	resetSignal?: number;
}

export function BizySidePanelChatView({ onOpenHistory, resetSignal = 0 }: BizySidePanelChatViewProps) {
	const { t } = useTranslation('admin_bizy');
	const { t: tMeetIntro } = useTranslation('one_on_ones', { keyPrefix: 'bizy_intro' });
	const { track } = useAnalytics();
	const toast = useToast();
	const { mutate: submitFeedback } = useBizyFeedbackMutation();
	const { mutate: deleteConversation } = useBizyDeleteConversationMutation();
	const { mutate: updateConversation } = useBizyUpdateConversationMutation();
	const { mutate: generateTitle } = useBizyTitleMutation();
	const pageContext = useBizyPageContextStore((state) => state.context);
	const pageType = pageContext.pageType;
	const {
		messages,
		conversationId,
		isLoading,
		isTimedOut,
		isOtherTabLoading,
		isLoadingConversation,
		bizyEmotion,
		activeToolCalls,
		sendMessage,
		resetChat,
		retryLastMessage,
		updateMessageFeedback,
		loadConversation,
	} = useBizySidePanelChat();
	const queryClient = useQueryClient();
	const panelView = useBizySidePanelStore((state) => state.view);

	const [inputValue, setInputValue] = useState('');
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
	const [titleOverride, setTitleOverride] = useState<string | undefined>();
	const [isBizyHovered, setIsBizyHovered] = useState(false);
	const [isInputFocused, setIsInputFocused] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const lastUserMsgRef = useRef<HTMLDivElement>(null);
	const shouldScrollRef = useRef(false);
	const prevResetSignalRef = useRef(resetSignal);

	const handleTranscript = useCallback((text: string) => {
		if (inputRef.current) {
			// Write to the DOM element first so scrollHeight reflects the new
			// content before we measure. setInputValue only schedules a React
			// re-render; the DOM still has the old value at measurement time.
			inputRef.current.value = text;
			inputRef.current.style.height = 'auto';
			inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
		}
		setInputValue(text);
	}, []);
	const handleVoiceStart = useCallback(() => {
		track(AnalyticsEvent.Bizy.VoiceInputUsed, { path: window.location.pathname });
	}, [track]);
	const handleVoiceError = useCallback(
		(reason: string) => {
			track(AnalyticsEvent.Bizy.VoiceInputError, { reason, path: window.location.pathname });
		},
		[track]
	);
	const {
		isRecording: isVoiceRecording,
		isStarting: isVoiceStarting,
		startRecording: startVoice,
		stopRecording: stopVoice,
	} = useBizyVoiceInput({ onTranscript: handleTranscript, onStart: handleVoiceStart, onError: handleVoiceError });

	useEffect(() => {
		if (resetSignal <= 0 || resetSignal === prevResetSignalRef.current) return;
		prevResetSignalRef.current = resetSignal;
		if (isVoiceRecording) void stopVoice();
		resetChat();
		setInputValue('');
		setTitleOverride(undefined);
	}, [isVoiceRecording, resetChat, resetSignal, stopVoice]);

	const lastUserMsgIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'user') return i;
		}
		return -1;
	}, [messages]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		const inner = container?.firstElementChild as HTMLElement | null;
		if (!container || !inner || panelView !== 'chat') return;

		if (shouldScrollRef.current) {
			shouldScrollRef.current = false;
			const el = lastUserMsgRef.current;
			if (el) {
				inner.style.paddingBottom = `${container.clientHeight}px`;
				const scrollTarget = el.offsetTop - 36;
				container.scrollTo({ top: scrollTarget, behavior: 'instant' });
			}
		}

		const currentPadding = parseFloat(inner.style.paddingBottom) || 0;
		if (currentPadding > 0) {
			const contentHeight = container.scrollHeight - currentPadding;
			const maxScrollWithoutPadding = contentHeight - container.clientHeight;
			const needed = Math.max(0, container.scrollTop - maxScrollWithoutPadding);
			inner.style.paddingBottom = `${needed}px`;
		}
	}, [messages, panelView]);

	const { data: recentData } = useBizyUserConversations({ per_page: RECENT_CONVERSATIONS_LIMIT });
	const recentConversations = useMemo(() => (recentData?.[0] ?? []).slice(0, RECENT_CONVERSATIONS_LIMIT), [recentData]);

	// Set title from server/cache when conversationId changes or when we get new data for the same conversation (e.g. page
	// refresh: effect ran before recentConversations loaded; when list loads, allow filling in server title). Skip only when
	// same conversation and no title to apply, so we don't overwrite with nothing after a refetch.
	const prevConversationIdForTitleRef = useRef<string | undefined>();
	useEffect(() => {
		if (!conversationId) return;
		const serverTitle = recentConversations.find((c) => c.conversationId === conversationId)?.conversationTitle;
		const cached = serverTitle || getCachedTitle(conversationId);
		const conversationChanged = prevConversationIdForTitleRef.current !== conversationId;
		if (conversationChanged) {
			prevConversationIdForTitleRef.current = conversationId;
		} else if (!cached) {
			// Same conversation, no new title data — don't overwrite (avoids reverting to wrong title on refetch)
			return;
		}
		setTitleOverride(cached ?? undefined);
	}, [conversationId, recentConversations]);

	// Keep the conversations list fresh so the dropdown includes the current chat
	const prevConversationIdRef = useRef<string | undefined>();
	useEffect(() => {
		if (conversationId && conversationId !== prevConversationIdRef.current) {
			prevConversationIdRef.current = conversationId;
			queryClient.invalidateQueries(bizyUserConversationsQueryKey());
		}
	}, [conversationId, queryClient]);

	const firstUserMessage = useMemo(() => messages.find((m) => m.role === 'user')?.content, [messages]);
	const generatedTitle = getActiveConversationTitle(firstUserMessage);
	const conversationTitle = titleOverride ?? generatedTitle;

	// Generate AI title after the first assistant response completes.
	// Skip if the conversation already has a server-side or cached title.
	const hasRequestedTitleRef = useRef<string | undefined>();
	const manuallyRenamedRef = useRef(false);
	const conversationIdRef = useRef(conversationId);
	conversationIdRef.current = conversationId;

	useEffect(() => {
		if (!conversationId || !firstUserMessage || titleOverride) return;
		if (hasRequestedTitleRef.current === conversationId) return;

		const firstAssistant = messages.find((m) => m.role === 'assistant' && m.id);
		if (!firstAssistant) return;

		const requestConversationId = conversationId;
		hasRequestedTitleRef.current = conversationId;
		manuallyRenamedRef.current = false;
		generateTitle(
			{ message: firstUserMessage },
			{
				onSuccess: (data) => {
					if (data.title && !manuallyRenamedRef.current && conversationIdRef.current === requestConversationId) {
						setTitleOverride(data.title);
						setCachedTitle(requestConversationId, data.title);
						updateConversation({ conversationId: requestConversationId, title: data.title });
						track(AnalyticsEvent.Bizy.ConversationTitleGenerated, { path: window.location.pathname });
					}
				},
			}
		);
	}, [conversationId, firstUserMessage, titleOverride, messages, generateTitle, updateConversation, track]);

	const company = useCurrentCompany();
	const user = useCurrentUser();
	const isCanceled = company.status === 'non_renewing';
	const isAdmin = user.admin;
	const displayName = user.preferred_first_name || user.first_name;
	const segment = getAudienceSegment(company.status, isAdmin);
	const isTrial = segment === 'trial_admin' || segment === 'trial_non_admin';
	const { data: activationState } = useBizyActivationState(isTrial);
	const promptName = useMemo(() => {
		if (pageType === '1on1') {
			const name = pageContext.pageName.replace('1:1 with ', '');
			if (name && name !== pageContext.pageName) return name;
		}
		return undefined;
	}, [pageType, pageContext.pageName]);
	const isPanelOpen = useBizySidePanelStore((state) => state.isOpen);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const isEmptyState = messages.length === 0 && !isLoading;

	const {
		pendingRequest: pendingFeatureRequest,
		showConflictDialog: showExternalOpenConflict,
		handleConfirmNewConversation,
		handleKeepCurrentConversation,
	} = useBizyExternalOpenAutoSend({
		isPanelOpen,
		isEmptyState,
		sendMessage,
		resetChat,
		shouldScrollRef,
	});

	const conversationPathRef = useRef<string | null>(null);
	const [showPagePrompts, setShowPagePrompts] = useState(false);
	useEffect(() => {
		if (isEmptyState) {
			setShowPagePrompts(false);
			conversationPathRef.current = null;
			return;
		}
		if (conversationPathRef.current === null) {
			conversationPathRef.current = window.location.pathname;
		}
		setShowPagePrompts(window.location.pathname !== conversationPathRef.current);
	}, [isEmptyState, pageContext]);

	const prevConversationIdForPromptsRef = useRef(conversationId);
	useEffect(() => {
		const prev = prevConversationIdForPromptsRef.current;
		prevConversationIdForPromptsRef.current = conversationId;
		if (prev && conversationId && prev !== conversationId) {
			conversationPathRef.current = null;
			setShowPagePrompts(false);
		}
	}, [conversationId]);

	const showMeetIntro = pageType === '1on1_list';
	const isGreetingContextVisible = isEmptyState && isPanelOpen && !showMeetIntro && panelView === 'chat';
	const { variant: greetingVariant, impressionCount } = useBizyGreeting(isGreetingContextVisible, isCanceled, isAdmin);
	const greetingKey = `greeting.${
		greetingVariant === 'canceled_admin' ? 'canceledAdmin' : greetingVariant === 'canceled_non_admin' ? 'canceledNonAdmin' : greetingVariant
	}`;
	const greetingText = t(greetingKey, { name: displayName });

	const hasTrackedGreetingRef = useRef(false);
	useEffect(() => {
		if (isGreetingContextVisible && !hasTrackedGreetingRef.current) {
			hasTrackedGreetingRef.current = true;
			track(AnalyticsEvent.Bizy.GreetingShown, {
				variant: greetingVariant,
				impressionCount,
				path: window.location.pathname,
			});
		}
		if (!isGreetingContextVisible) {
			hasTrackedGreetingRef.current = false;
		}
	}, [isGreetingContextVisible, greetingVariant, impressionCount, track]);

	const promptChips = useMemo(
		() => getPromptChips(pageType, segment, activationState, isAdmin, 3, pageContext.pageData).map((key) => t(key, { name: promptName })),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pageType, resetSignal, segment, activationState, isAdmin, promptName, pageContext.pageData]
	);
	const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

	useEffect(() => {
		if ((!isPanelOpen || panelView !== 'chat') && (isVoiceRecording || isVoiceStarting)) {
			void stopVoice();
		}
	}, [isPanelOpen, panelView, isVoiceRecording, isVoiceStarting, stopVoice]);

	const lastMessage = messages[messages.length - 1];
	const isStreaming = isLoading && lastMessage?.role === 'assistant' && !lastMessage.id;
	const showThinkingIndicator = isLoading && !isStreaming && lastMessage?.role !== 'assistant';
	const isBizyExcited = isBizyHovered || (isEmptyState && isInputFocused);
	const bizyState: BizyState = isStreaming ? 'buzzing' : isLoading ? 'searching' : isBizyExcited ? 'excited' : (bizyEmotion ?? 'curious');

	const thinkingPhrases = useMemo(() => {
		const raw = t('thinkingPhrases', { returnObjects: true });
		return Array.isArray(raw) ? (raw as string[]) : [t('thinking')];
	}, [t]);
	const [thinkingPhraseIdx, setThinkingPhraseIdx] = useState(() => Math.floor(Math.random() * thinkingPhrases.length));
	useEffect(() => {
		if (!isLoading || thinkingPhrases.length <= 1) {
			setThinkingPhraseIdx((prev) => {
				if (thinkingPhrases.length <= 1) return 0;
				let next = Math.floor(Math.random() * (thinkingPhrases.length - 1));
				if (next >= prev) next++;
				return next;
			});
			return;
		}
		const id = setInterval(() => {
			setThinkingPhraseIdx((prev) => {
				let next = Math.floor(Math.random() * (thinkingPhrases.length - 1));
				if (next >= prev) next++;
				return next;
			});
		}, 4000);
		return () => clearInterval(id);
	}, [isLoading, thinkingPhrases.length]);

	const toolProgressItems = activeToolCalls
		.map(getToolProgressKey)
		.filter((key): key is string => key !== null)
		.map((key) => t(key));

	const lastCompleteAssistantIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const isLastMsg = i === messages.length - 1;
			const isStreamingMsg = isLastMsg && isLoading && messages[i].role === 'assistant';
			if (messages[i].role === 'assistant' && messages[i].id && !isStreamingMsg) return i;
		}
		return -1;
	}, [messages, isLoading]);

	const inputDisabled = isLoading || isOtherTabLoading;

	const handleVoiceToggle = useCallback(async () => {
		if (isVoiceRecording) {
			await stopVoice();
		} else {
			await startVoice();
		}
	}, [isVoiceRecording, startVoice, stopVoice]);

	const handleSend = useCallback(() => {
		if (!inputValue.trim() || inputDisabled) return;
		if (isVoiceRecording) void stopVoice();
		sendMessage(inputValue.trim());
		setInputValue('');
		shouldScrollRef.current = true;
		if (inputRef.current) {
			inputRef.current.style.height = 'auto';
			inputRef.current.focus();
		}
	}, [inputValue, inputDisabled, isVoiceRecording, stopVoice, sendMessage]);

	const handleResetConversation = useCallback(() => {
		if (isVoiceRecording) void stopVoice();
		track(AnalyticsEvent.Bizy.ConversationReset, {
			conversationLength: messages.length,
			path: window.location.pathname,
		});
		resetChat();
		setInputValue('');
		setTitleOverride(undefined);
		inputRef.current?.focus();
	}, [isVoiceRecording, messages.length, resetChat, stopVoice, track]);

	const handleSelectPrompt = useCallback(
		(prompt: string) => {
			sendMessage(prompt);
			shouldScrollRef.current = true;
		},
		[sendMessage]
	);

	const deleteDialog = useDisclosure();
	const deleteCancelRef = useRef<HTMLButtonElement>(null);
	const conflictKeepRef = useRef<HTMLButtonElement>(null);
	const [deletingConversationId, setDeletingConversationId] = useState<string | undefined>();

	const handleConfirmDelete = useCallback(() => {
		if (isVoiceRecording) void stopVoice();
		const idToDelete = deletingConversationId ?? conversationId;
		if (idToDelete) {
			const isActive = idToDelete === conversationId;
			deleteConversation(idToDelete, {
				onSuccess: () => {
					removeCachedTitle(idToDelete);
					if (isActive) {
						clearBizySession();
						resetChat();
						setInputValue('');
						setTitleOverride(undefined);
					}
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatSuccess'), status: 'info', duration: 3000 });
					track(AnalyticsEvent.Bizy.ConversationDeleted, { path: window.location.pathname });
				},
				onError: () => {
					setDeletingConversationId(undefined);
					deleteDialog.onClose();
					toast({ title: t('sidePanel.deleteChatError'), status: 'error', duration: 3000 });
				},
			});
		} else {
			resetChat();
			setInputValue('');
			setTitleOverride(undefined);
			setDeletingConversationId(undefined);
			deleteDialog.onClose();
		}
	}, [conversationId, deletingConversationId, isVoiceRecording, resetChat, deleteConversation, deleteDialog, stopVoice, toast, track, t]);

	const handlePositiveFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackPositive, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: 'positive' }, { onSuccess: () => updateMessageFeedback(messageId, 'positive') });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleNegativeFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackNegativeStarted, { path: window.location.pathname });
			setFeedbackMessageId(messageId);
			setIsFeedbackModalOpen(true);
		},
		[track]
	);

	const handleClearFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackCleared, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: null }, { onSuccess: () => updateMessageFeedback(messageId, undefined) });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleFeedbackModalSubmit = useCallback(
		(category: string, details?: string) => {
			if (!feedbackMessageId) return;
			track(AnalyticsEvent.Bizy.FeedbackNegativeSubmitted, { category, path: window.location.pathname });
			submitFeedback(
				{
					messageId: feedbackMessageId,
					feedback_type: 'negative',
					feedback_category: category,
					feedback_details: details,
				},
				{
					onSuccess: () => {
						updateMessageFeedback(feedbackMessageId, 'negative');
						setIsFeedbackModalOpen(false);
						setFeedbackMessageId(null);
					},
				}
			);
		},
		[feedbackMessageId, submitFeedback, updateMessageFeedback, track]
	);

	const [titleMenuOpen, setTitleMenuOpen] = useState(false);
	const [dropdownRenameId, setDropdownRenameId] = useState<string | undefined>();
	const [dropdownRenameValue, setDropdownRenameValue] = useState('');
	const renameCommittedRef = useRef(false);

	const handleDropdownRename = useCallback(
		(id: string) => {
			renameCommittedRef.current = false;
			const conv = recentConversations.find((c) => c.conversationId === id);
			const title = conv?.conversationTitle || getCachedTitle(id) || (conv ? generateConversationTitle(conv.firstUserMessage) : '');
			setDropdownRenameValue(title);
			setDropdownRenameId(id);
		},
		[recentConversations]
	);

	const handleDropdownFinishRename = useCallback(() => {
		if (renameCommittedRef.current) return;
		renameCommittedRef.current = true;
		if (dropdownRenameId && dropdownRenameValue.trim()) {
			setCachedTitle(dropdownRenameId, dropdownRenameValue.trim());
			updateConversation({ conversationId: dropdownRenameId, title: dropdownRenameValue.trim() });
			track(AnalyticsEvent.Bizy.ConversationRenamed, { path: window.location.pathname });
			if (dropdownRenameId === conversationId) {
				setTitleOverride(dropdownRenameValue.trim());
				manuallyRenamedRef.current = true;
			}
		}
		setDropdownRenameId(undefined);
		setDropdownRenameValue('');
	}, [dropdownRenameId, dropdownRenameValue, conversationId, updateConversation, track]);

	const handleDropdownDelete = useCallback(
		(id: string) => {
			setTitleMenuOpen(false);
			setDeletingConversationId(id);
			deleteDialog.onOpen();
		},
		[deleteDialog]
	);

	const titleElement = (
		<Menu isOpen={titleMenuOpen} onOpen={() => setTitleMenuOpen(true)} onClose={() => setTitleMenuOpen(false)} autoSelect={false}>
			<MenuButton
				as={Button}
				variant="ghost"
				size="sm"
				pl={2.5}
				pr={1.5}
				h="auto"
				minH="28px"
				maxW="100%"
				iconSpacing={1}
				rightIcon={<CheveronSmallDownOutline boxSize={3.5} color="secondary.fg" flexShrink={0} />}
				_hover={{ bg: 'secondary.bgAlpha' }}
			>
				<Text fontSize="md" fontWeight="semibold" color="text.default" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{conversationTitle}
				</Text>
			</MenuButton>
			<MenuList minW="220px" maxH="320px" overflowY="auto" zIndex="popover">
				{recentConversations.length > 0 && (
					<>
						<Text px={3} py={1.5} fontSize="sm" fontWeight="medium" color="text.softest">
							{t('sidePanel.recentConversations')}
						</Text>
						{recentConversations.map((conversation) => {
							const isActive = conversation.conversationId === conversationId;
							const isDropdownRenaming = dropdownRenameId === conversation.conversationId;
							const itemTitle =
								getCachedTitle(conversation.conversationId) || conversation.conversationTitle || generateConversationTitle(conversation.firstUserMessage);

							return (
								<Flex
									key={conversation.conversationId}
									align="center"
									px={isDropdownRenaming ? 2 : 3}
									py={isDropdownRenaming ? 1 : 1.5}
									borderRadius="sm"
									cursor="pointer"
									bg={isActive && !isDropdownRenaming ? 'secondary.bgAlpha' : undefined}
									_hover={isDropdownRenaming ? undefined : { bg: 'bg.canvas' }}
									role="group"
									onClick={() => {
										if (!isDropdownRenaming) {
											setTitleMenuOpen(false);
											if (!isActive) loadConversation(conversation.conversationId);
										}
									}}
								>
									<Box flex={1} minW={0}>
										{isDropdownRenaming ? (
											<Input
												value={dropdownRenameValue}
												onChange={(e) => setDropdownRenameValue(e.target.value)}
												onBlur={handleDropdownFinishRename}
												onKeyDown={(e) => {
													if (e.key === 'Enter') handleDropdownFinishRename();
													if (e.key === 'Escape') {
														e.stopPropagation();
														renameCommittedRef.current = true;
														setDropdownRenameId(undefined);
														setDropdownRenameValue('');
													}
												}}
												fontSize="md"
												variant="outline"
												w="100%"
												h={9}
												px={2}
												borderRadius="md"
												borderColor="primary.fg"
												borderWidth="1.5px"
												_focus={{ borderColor: 'primary.fg', boxShadow: 'none' }}
												autoFocus
												onClick={(e) => e.stopPropagation()}
											/>
										) : (
											<Text noOfLines={1} color="text.default" fontWeight={isActive ? 'medium' : 'normal'}>
												{itemTitle}
											</Text>
										)}
									</Box>
									{!isDropdownRenaming && (
										<Box flexShrink={0} ml={1} opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.1s">
											<Menu>
												<Tooltip label={t('sidePanel.moreActions')} hasArrow={false}>
													<MenuButton
														as={IconButton}
														aria-label={t('sidePanel.moreActions')}
														icon={<DotsHorizontalOutline boxSize={3.5} />}
														variant="ghost"
														size="xs"
														minW={6}
														h={6}
														color="text.softer"
														_hover={{ bg: 'secondary.bgAlphaHover' }}
														onClick={(e: React.MouseEvent) => e.stopPropagation()}
														borderRadius="lg"
													/>
												</Tooltip>
												<MenuList minW="120px" zIndex="popover">
													<MenuItem
														icon={<PencilOutline boxSize={5} />}
														onClick={(e) => {
															e.stopPropagation();
															handleDropdownRename(conversation.conversationId);
														}}
													>
														{t('sidePanel.renameChat')}
													</MenuItem>
													<MenuItem
														color="error.fg"
														icon={<TrashOutline boxSize={5} />}
														onClick={(e) => {
															e.stopPropagation();
															handleDropdownDelete(conversation.conversationId);
														}}
													>
														{t('sidePanel.deleteChat')}
													</MenuItem>
												</MenuList>
											</Menu>
										</Box>
									)}
								</Flex>
							);
						})}
						<MenuDivider />
					</>
				)}
				<MenuItem onClick={onOpenHistory} color="text.interactive">
					{t('sidePanel.viewAllHistory')}
				</MenuItem>
			</MenuList>
		</Menu>
	);

	return (
		<Flex direction="column" h="100%">
			<BizySidePanelHeader
				left={titleElement}
				right={
					<HStack spacing={0}>
						<Tooltip label={t('sidePanel.newChat')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.newChat')}
								icon={<PencilAltOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={handleResetConversation}
							/>
						</Tooltip>
						<Tooltip label={t('sidePanel.close')} hasArrow={false}>
							<IconButton
								aria-label={t('sidePanel.close')}
								icon={<XOutline boxSize={5} />}
								variant="ghost"
								size="sm"
								color="secondary.fg"
								_hover={{ bg: 'secondary.bgAlpha' }}
								onClick={() => closePanel()}
							/>
						</Tooltip>
					</HStack>
				}
			/>

			<Flex direction="column" flex={1} minH={0}>
				<Box position="relative" flex={1} overflow="hidden">
					<Box
						position="absolute"
						top={0}
						left={0}
						right={0}
						height="32px"
						background="linear-gradient(to bottom, var(--chakra-colors-bg-canvas) 0%, transparent 100%)"
						pointerEvents="none"
						opacity={isScrolledFromTop ? 1 : 0}
						transition="opacity 0.15s ease-in-out"
						zIndex={1}
					/>
					<Box
						ref={scrollContainerRef}
						height="100%"
						overflowY="auto"
						onScroll={(e: React.UIEvent<HTMLDivElement>) => setIsScrolledFromTop(e.currentTarget.scrollTop > 10)}
						sx={{ scrollPaddingTop: '36px' }}
						{...(isEmptyState ? { display: 'flex', flexDirection: 'column' as const } : {})}
					>
						<Flex direction="column" gap={2} px={4} pt={3} flex={isEmptyState ? 1 : undefined} minH={0}>
							{isLoadingConversation && (
								<Flex direction="column" align="center" justify="center" flex={1} py={8} gap={3}>
									<Spinner size="lg" color="primary.fg" />
									<Text fontSize="sm" color="text.soft">
										{t('sidePanel.loadingConversation')}
									</Text>
								</Flex>
							)}
							{!isLoadingConversation && isEmptyState && isPanelOpen && panelView === 'chat' && (
								<>
									{showMeetIntro && (
										<Flex direction="column" gap={5} px={2}>
											<Flex direction="column" gap={2}>
												<Text fontSize="md" fontWeight="semibold" color="text.default">
													{tMeetIntro('greeting')}
												</Text>
												<Text fontSize="sm" color="text.soft" lineHeight="tall">
													{tMeetIntro('introduction')}
												</Text>
											</Flex>
											<Flex direction="column" gap={4}>
												<Text fontSize="sm" fontWeight="bold" color="text.default">
													{tMeetIntro('capabilities_header')}
												</Text>
												<Flex direction="column" gap={4}>
													<HStack spacing={3} align="flex-start">
														<SearchOutline boxSize={5} color="text.soft" flexShrink={0} mt={0.5} />
														<Flex direction="column">
															<Text fontWeight="semibold" fontSize="sm" color="text.default">
																{tMeetIntro('search_title')}
															</Text>
															<Text fontSize="sm" color="text.soft">
																{tMeetIntro('search_description')}
															</Text>
														</Flex>
													</HStack>
													<HStack spacing={3} align="flex-start">
														<LightBulbOutline boxSize={5} color="text.soft" flexShrink={0} mt={0.5} />
														<Flex direction="column">
															<Text fontWeight="semibold" fontSize="sm" color="text.default">
																{tMeetIntro('suggestions_title')}
															</Text>
															<Text fontSize="sm" color="text.soft">
																{tMeetIntro('suggestions_description')}
															</Text>
														</Flex>
													</HStack>
													<HStack spacing={3} align="flex-start">
														<SparklesOutline boxSize={5} color="text.soft" flexShrink={0} mt={0.5} />
														<Flex direction="column">
															<Text fontWeight="semibold" fontSize="sm" color="text.default">
																{tMeetIntro('grow_title')}
															</Text>
															<Text fontSize="sm" color="text.soft">
																{tMeetIntro('grow_description')}
															</Text>
														</Flex>
													</HStack>
												</Flex>
											</Flex>
											<Text fontSize="sm" color="text.soft">
												{tMeetIntro('cta')}
											</Text>
										</Flex>
									)}

									<Flex direction="column" align="center" justify="center" flex={1} py={6} gap={4}>
										<motion.div
											initial={{ scale: 0, opacity: 0 }}
											animate={{ scale: 1, opacity: 1 }}
											transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.25 }}
											onMouseEnter={() => setIsBizyHovered(true)}
											onMouseLeave={() => setIsBizyHovered(false)}
											style={{ cursor: 'pointer' }}
										>
											<Bizy state={bizyState} size="md" />
										</motion.div>

										{!showMeetIntro && (
											<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.45 }}>
												<Text fontSize="sm" color="text.soft" textAlign="center" maxW="260px" lineHeight="tall">
													{greetingText}
												</Text>
											</motion.div>
										)}
									</Flex>
								</>
							)}
							{!isLoadingConversation &&
								messages.map((message, index) => {
									const isUser = message.role === 'user';
									const isLast = index === messages.length - 1;
									const isStreamingThisMessage = isLast && isStreaming;
									const isComplete = !isStreamingThisMessage && !isUser && !!message.id;

									if (isUser) {
										return (
											<Flex
												key={message.key || index}
												ref={index === lastUserMsgIndex ? lastUserMsgRef : undefined}
												direction="column"
												alignItems="flex-end"
												alignSelf="flex-end"
												maxW="85%"
												role="group"
											>
												<Box
													bg="secondary.bgAlpha"
													borderRadius={12}
													borderBottomRightRadius={4}
													px={3}
													py={2}
													border="1px solid"
													borderColor="secondary.bgAlpha"
													w="fit-content"
												>
													<Text fontSize={{ base: 'md', sm: 'sm' }}>{message.content}</Text>
												</Box>
												<Box opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.15s ease-in-out" mt={1}>
													<UserMessageActions content={message.content} timestamp={message.timestamp} />
												</Box>
											</Flex>
										);
									}

									if (!isStreamingThisMessage && isNotesEdit(message)) {
										return (
											<Flex key={message.key || message.id || index} direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
												<BizyNotesEditMessage content={message.content} StreamingMessage={BizyStreamingMessage} />
												{isComplete && message.id && (
													<BizyMessageFeedback
														messageId={message.id}
														content={message.content}
														feedbackType={message.feedbackType}
														onPositiveFeedback={handlePositiveFeedback}
														onNegativeFeedback={handleNegativeFeedback}
														onClearFeedback={handleClearFeedback}
														isLatestReply={index === lastCompleteAssistantIndex}
													/>
												)}
											</Flex>
										);
									}

									const messageToolProgress = isStreamingThisMessage ? toolProgressItems : [];

									return (
										<Flex key={message.key || message.id || index} direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
											<BizyStreamingMessage
												content={message.content}
												isComplete={!isStreamingThisMessage}
												toolProgress={messageToolProgress}
												thinkingPhrase={thinkingPhrases[thinkingPhraseIdx]}
											/>
											{isComplete && message.id && (
												<BizyMessageFeedback
													messageId={message.id}
													content={message.content}
													feedbackType={message.feedbackType}
													onPositiveFeedback={handlePositiveFeedback}
													onNegativeFeedback={handleNegativeFeedback}
													onClearFeedback={handleClearFeedback}
													isLatestReply={index === lastCompleteAssistantIndex}
												/>
											)}
										</Flex>
									);
								})}

							{showThinkingIndicator && (
								<Box minH="20px" overflow="hidden" pl={1}>
									<AnimatePresence mode="wait" initial={false}>
										{toolProgressItems.length > 0 ? (
											toolProgressItems.map((item, i) => {
												const isLatest = i === toolProgressItems.length - 1;
												return (
													<motion.div
														key={`${i}-${item}`}
														initial={{ opacity: 0, y: 4 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -4 }}
														transition={{ duration: 0.35 }}
													>
														<Text
															fontSize="xs"
															fontWeight="medium"
															color="text.softest"
															sx={
																isLatest
																	? {
																			'@keyframes pulse': {
																				'0%, 100%': { opacity: 1 },
																				'50%': { opacity: 0.4 },
																			},
																			animation: 'pulse 1.5s ease-in-out infinite',
																		}
																	: { opacity: 0.6 }
															}
														>
															{item}
														</Text>
													</motion.div>
												);
											})
										) : (
											<motion.div
												key={thinkingPhraseIdx}
												initial={{ opacity: 0, y: 4 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -4 }}
												transition={{ duration: 0.35 }}
											>
												<Text
													fontSize="xs"
													fontWeight="medium"
													color="text.softest"
													sx={{
														'@keyframes pulse': {
															'0%, 100%': { opacity: 1 },
															'50%': { opacity: 0.4 },
														},
														animation: 'pulse 1.5s ease-in-out infinite',
													}}
												>
													{thinkingPhrases[thinkingPhraseIdx]}
												</Text>
											</motion.div>
										)}
									</AnimatePresence>
								</Box>
							)}

							{isTimedOut && (
								<Flex alignSelf="flex-start" direction="column" gap={1} pl={1}>
									<Text fontSize="sm" color="text.softer">
										{t('timeout.message')}
									</Text>
									<Button variant="link" size="sm" color="text.interactive" onClick={retryLastMessage} px={0} justifyContent="flex-start">
										{t('timeout.retry')}
									</Button>
								</Flex>
							)}

							{messages.length > 0 && (
								<motion.div
									layout
									transition={{ type: 'spring', stiffness: 200, damping: 25 }}
									style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0', cursor: 'pointer' }}
									onMouseEnter={() => setIsBizyHovered(true)}
									onMouseLeave={() => setIsBizyHovered(false)}
								>
									<Bizy state={bizyState} size="md" />
								</motion.div>
							)}
						</Flex>
					</Box>
				</Box>

				<Box px={2} pb={2} sx={{ paddingBottom: 'max(var(--chakra-space-2), env(safe-area-inset-bottom))' }}>
					{isEmptyState && <BizySidePanelSuggestedPrompts prompts={promptChips} onSelectPrompt={handleSelectPrompt} layout="vertical" />}
					{!isEmptyState && showPagePrompts && !isLoading && (
						<BizySidePanelSuggestedPrompts prompts={promptChips} onSelectPrompt={handleSelectPrompt} layout="horizontal" />
					)}
					{isOtherTabLoading && (
						<Text fontSize="xs" color="text.softest" px={1} pb={1}>
							{t('otherTab.responding')}
						</Text>
					)}
					<Box position="relative">
						<BizyAmbientGlow />
						<Flex direction="column" gap={1} border="1px solid" borderColor="border.default" borderRadius="lg" p={3} bg="bg.surface" position="relative">
							<Textarea
								ref={inputRef}
								value={inputValue}
								onChange={(e) => {
									setInputValue(e.target.value);
									e.target.style.height = 'auto';
									e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault();
										handleSend();
									}
								}}
								onFocus={() => setIsInputFocused(true)}
								onBlur={() => setIsInputFocused(false)}
								placeholder={pendingFeatureRequest?.promptHint || t('inputPlaceholder')}
								aria-label={t('ariaLabel.chatMessageInput')}
								variant="unstyled"
								fontSize={{ base: 'md', sm: 'sm' }}
								rows={1}
								resize="none"
								minH="28px"
								maxH="120px"
								lineHeight="20px"
								borderRadius={0}
								px={0}
								py="2px"
							/>
							<Flex align="center" justify="space-between">
								<HStack spacing={1} bg="secondary.bgAlpha" borderRadius="6px" px={1.5} py={0.5}>
									<Box as={pageType === '1on1' ? ChatAlt2Outline : GlobeAltOutline} boxSize={3} color="text.softest" sx={{ '& path': { strokeWidth: 1 } }} />
									<Text fontSize="xs" color="text.softest">
										{pageContext.pageName}
									</Text>
								</HStack>
								<HStack spacing={0}>
									{(!inputValue.trim() || isVoiceRecording || isVoiceStarting) && (
										<Tooltip label={t(isVoiceRecording ? 'voiceInput.stop' : 'voiceInput.start')} hasArrow={false}>
											<IconButton
												aria-label={t(isVoiceRecording ? 'voiceInput.stop' : 'voiceInput.start')}
												icon={isVoiceRecording ? <StopSolid boxSize={4} /> : <MicrophoneOutline boxSize={4} />}
												borderRadius="full"
												size="sm"
												minW={8}
												h={8}
												bg={isVoiceRecording ? 'error.bg' : 'transparent'}
												color={isVoiceRecording ? 'error.fg' : 'text.softest'}
												onClick={handleVoiceToggle}
												isLoading={isVoiceStarting}
												isDisabled={inputDisabled}
												_hover={{ bg: isVoiceRecording ? 'error.bgHover' : 'secondary.bgAlpha' }}
												_disabled={{ opacity: 0.2, cursor: 'not-allowed' }}
											/>
										</Tooltip>
									)}
									<IconButton
										aria-label={t('ariaLabel.sendMessage')}
										icon={<ArrowUpOutline boxSize={4} />}
										borderRadius="full"
										size="sm"
										minW={8}
										h={8}
										bg="secondary.bg"
										color="secondary.fgOnSurface"
										onClick={handleSend}
										isDisabled={!inputValue.trim() || inputDisabled}
										_hover={{ bg: 'secondary.bgHover' }}
										_disabled={{ opacity: 0.2, cursor: 'not-allowed' }}
									/>
								</HStack>
							</Flex>
						</Flex>
					</Box>
				</Box>
			</Flex>

			<BizyFeedbackModal
				isOpen={isFeedbackModalOpen}
				onClose={() => {
					track(AnalyticsEvent.Bizy.FeedbackNegativeCancelled, { path: window.location.pathname });
					setIsFeedbackModalOpen(false);
					setFeedbackMessageId(null);
				}}
				onSubmit={handleFeedbackModalSubmit}
			/>

			<AlertDialog leastDestructiveRef={deleteCancelRef as RefObject<HTMLButtonElement>} isOpen={deleteDialog.isOpen} onClose={deleteDialog.onClose} isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.deleteChatTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.deleteChatConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={deleteCancelRef} variant="outline" colorScheme="secondary" onClick={deleteDialog.onClose}>
								{t('sidePanel.cancel')}
							</Button>
							<Button variant="solid" colorScheme="danger" onClick={handleConfirmDelete}>
								{t('sidePanel.deleteChat')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>

			<AlertDialog
				leastDestructiveRef={conflictKeepRef as RefObject<HTMLButtonElement>}
				isOpen={showExternalOpenConflict}
				onClose={handleKeepCurrentConversation}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.newConversationTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.newConversationConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={conflictKeepRef} variant="outline" colorScheme="secondary" onClick={handleKeepCurrentConversation}>
								{t('sidePanel.keepCurrent')}
							</Button>
							<Button variant="solid" colorScheme="primary" onClick={handleConfirmNewConversation}>
								{t('sidePanel.startNew')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}
